import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";

// store
import useAuthStore from "store/useAuthStore";

// components
import { SBATag } from "components/LibraryComponents/Components";

// types
import { AccordionBusinessItem, BusinessItem } from "types/results-panel.types";

// utils
import { padding } from "@mui/system";
import moment from "moment";
import { getCapabilitiesNarrativeLink, refineCertificationTags, splitByEveryOtherComma, formatUnixTimestamp, formatAddress, getLegalStructureName } from "utils/helper";
import { Permissions } from "constants/permissions";
import { Icon } from "@trussworks/react-uswds";

function formatAsCurrency(number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(number);
}

const trunceText = (text: string, length: number) => {
  if (!text) return null;

  if (text.length > length) {
    return text.substring(0, length) + "...";
  }
  return text;
};

enum CertificationHeaderToName {
  "cert_8a" = "8(a) Certification",
  "cert_8aJV" = "8(a) Joint Venture",
  "cert_HZ" = "HUBZone Certification",
  "cert_WOSB" = "Women-Owned Small Business (WOSB)",
  "cert_EDWOSB" = "Economically Disadvantaged Women-Owned Small Business (EDWOSB)",
  "cert_VOSB" = "Veteran-Owned Small Business",
  "cert_VOSBJV" = "Veteran-Owned Small Business Joint Venture",
  "cert_SDVOSB" = "Service-Disabled Veteran-Owned Small Business",
  "cert_SDVOSBJV" = "Service-Disabled Veteran-Owned Small Business Joint Venture",
}

const format = {
  'certification_status': (data, id) => {
    const mapCertStatus = {
      'A': 'Active',
      'E': 'Expired',
      'P': 'Pending',
      'I': 'Expired',
      'S': 'Suspended'
    };
    return data.active_certifications
      ?.filter(cert => cert?.name === CertificationHeaderToName[id])
      ?.map(cert => mapCertStatus[cert?.cert_active] ?? cert?.cert_active)[0];
  },
  'certification_date': (data, id, _isStart = true) => {
    let certDate = data.active_certifications
      ?.filter(cert => cert?.name === CertificationHeaderToName[id])
      ?.map(cert => _isStart === true ? cert?.start_date : cert?.exit_date)[0];
    return !certDate ? "" : moment(certDate).format("MM/D/YYYY");
  },
  'tag': (data) => {
    if (data === "Active") {
      return <SBATag name={data} isPending={false} />;
    }

    if (data === "Suspended") {
      return <SBATag name={data} isPending={false} isSuspended={true} background="#FFBE2E" />;
    }

    if (data === "Expired") {
      return <SBATag name={"Previously certified"} isPending={false} isExpired={true} background="#565C65" />;
    }

    return null;
  }
}

const columnHelper = createColumnHelper<BusinessItem | AccordionBusinessItem>();

const columns = [
  columnHelper.accessor("legal_business_name", {
    id: "legal_business_name",
    header: () => "Small business name",
    cell: (props) => {
      const data = props.row.original;

      return <Link to={`/profile/${data.uei}/${data.cage_code}?page=${props.table.getState().pagination.pageIndex + 1}`}>{data.legal_business_name}</Link>;
    },
  }),

  columnHelper.accessor("capabilities_narrative", {
    id: "capabilities_narrative",
    header: () => "Capabilities narrative",
    cell: (props) => {
      const data = props.row.original;
      // const link = getCapabilitiesNarrativeLink(data);

      return (
        <>
          {trunceText(data.capabilities_narrative, 150)}
          <br />
          {data?.capabilities_link ? (
            <a
              href={data?.capabilities_link}
              target="_blank"
              rel="noreferrer"
              className="usa-link usa-link--external"
            >
              Capabilities statement
            </a>
          ) : null}
        </>
      );
    },
  }),

  columnHelper.accessor("certs", {
    id: "certs",
    header: () => "Active SBA certifications",
    cell: (props) => {
      const data = props.row.original;

      const activeCerts = data?.certs.filter(cert => cert.active || cert.pending);

      let activeTags: JSX.Element[] = [];

      if (activeCerts?.length > 0) {
        activeCerts.map((cert, i) => {
          activeTags.push(<SBATag key={i} name={cert.pending ? `${cert.name} Pending` : cert.name} isPending={cert.pending} />)
        })
        return activeTags
      }

      return <p className="no-active-sba">No active SBA certifications</p>;
    },
  }),

  columnHelper.accessor("contact_person", {
    id: "contact_person",
    header: () => "Contact person",
    cell: (props) => {
      const data = props.row.original;
      const userRole = useAuthStore.getState().user?.role;

      const hideEmailInformation = !data?.display_email;
      const isPrivileged = userRole
        ? Permissions[userRole]?.privilegedSearch
        : false;

      return (
        <>
          {data.contact_person}
          <br />
          {(!hideEmailInformation || isPrivileged) && (
            <a href={`mailto:${data.email}`} className="usa-link usa-link">
              {data.email}
            </a>
          )}

          {hideEmailInformation && (
            <div className={"global__hide-information"}>
              <Icon.Error aria-label="explanation icon" />
              <p>
                The business owner has hidden this email address from public
                searches
              </p>
            </div>
          )}
        </>
      );
    },
  }),

  columnHelper.accessor("address_1", {
    id: "address_1",
    header: () => "Address",
    cell: (props) => {
      const data = props.row.original;
      const userRole = useAuthStore.getState().user?.role;

      const hideAddrInformation = !data?.display_address;
      const isPrivileged = userRole
        ? Permissions[userRole]?.privilegedSearch
        : false;

      const address = formatAddress(data);
      let googleMapsLink = `https://www.google.com/maps/place/${address}`;

      if (address) {
        return (
          <>
            {(!hideAddrInformation || isPrivileged) && (
              <a
                href={googleMapsLink}
                target="_blank"
                rel="noreferrer"
                className="usa-link usa-link--external"
              >
                {address}
              </a>
            )}
            
            {hideAddrInformation && (
              <div className={"global__hide-information"}>
                <Icon.Error aria-label="explanation icon" />
                <p>
                  The business owner has hidden this information from public
                  searches
                </p>
              </div>
            )}
          </>
        );
      } else if (hideAddrInformation) {
        return (
          <>
            <div className={"global__hide-information"}>
            <Icon.Error aria-label="explanation icon" />
            <p>
              The business owner has hidden this information from public
              searches
            </p>
          </div>
        </>
        );
      }
      else return null;
    },
  }),

  columnHelper.accessor("_rankingScore", {
    id: "_rankingScore",
    header: () => null,
    cell: () => null,
  })
];

const optionalColumns = [
  columnHelper.accessor("phone", {
    id: "phone",
    header: () => "Phone",
    cell: (props) => {
      const data = props.row.original;
      const userRole = useAuthStore.getState().user?.role;

      const hidePhoneInformation = !data?.display_phone;
      const isPrivileged = userRole
        ? Permissions[userRole]?.privilegedSearch
        : false;

      const phone = data.phone;
      const phone_ext = data.phone_ext;

      if (hidePhoneInformation && !phone && !phone_ext) 
        return (
          <>
            <div className={"global__hide-information"}>
              <Icon.Error aria-label="explanation icon" />
              <p>
                The business owner has hidden this information from public
                searches
              </p>
            </div>
          </>
        )

        else if (!phone && !phone_ext) return null;

      return (
        <>
          {(!hidePhoneInformation || isPrivileged) && (
            <>{phone_ext ? `${phone} ext. ${phone_ext}` : phone}</>
          )}

          {hidePhoneInformation && (
            <div className={"global__hide-information"}>
              <Icon.Error aria-label="explanation icon" />
              <p>
                The business owner has hidden this information from public
                searches
              </p>
            </div>
          )}
        </>
      );
    },
  }),

  columnHelper.accessor("fax", {
    id: "fax",
    header: () => "Fax",
    cell: (props) => {
      const data = props.row.original;
      const userRole = useAuthStore.getState().user?.role;

      const hideFaxInformation = !data?.display_fax;
      const isPrivileged = userRole
        ? Permissions[userRole]?.privilegedSearch
        : false;

      if (hideFaxInformation && !data.fax) 
        return (
          <>
            <div className={"global__hide-information"}>
              <Icon.Error aria-label="explanation icon" />
              <p>
                The business owner has hidden this information from public
                searches
              </p>
            </div>
          </>
        );
      
      else if (!data.fax) return;

      return (
        <>
          {(!hideFaxInformation || isPrivileged) && <>{data.fax}</>}

          {hideFaxInformation && (
            <div className={"global__hide-information"}>
              <Icon.Error aria-label="explanation icon" />
              <p>
                The business owner has hidden this information from public
                searches
              </p>
            </div>
          )}
        </>
      );
    },
  }),

  columnHelper.accessor("website", {
    id: "website",
    header: () => "Website",
    cell: (props) => {
      const data = props.row.original;
      const data_website = data.website?.indexOf("http") === -1 ? `http://${data.website}` : data.website;
      return (
        data.website && <a
          href={data_website}
          target="_blank"
          rel="noreferrer"
          className="usa-link usa-link--external"
        >
          {data.website}
        </a>
      );
    },
  }),

  columnHelper.accessor("uei", {
    id: "uei",
    header: () => "Unique Entity Identifier",
    cell: (props) => {
      const data = props.row.original;
      return data.uei;
    },
  }),

  columnHelper.accessor("last_update_date", {
    id: "last_update_date",
    header: () => "Last updated",
    cell: (props) => {
      const data = props.row.original;
      return data.last_update_date ? formatUnixTimestamp(data.last_update_date) : null;
    },
  }),

  columnHelper.accessor("dba_name", {
    id: "dba_name",
    header: () => "Business trade name",
    cell: (props) => {
      const data = props.row.original;
      return data.dba_name;
    },
  }),

  columnHelper.accessor("county", {
    id: "county",
    header: () => "County name",
    cell: (props) => {
      const data = props.row.original;
      return data.county;
    },
  }),

  columnHelper.accessor("county_code", {
    id: "county_code",
    header: () => "County code",
    cell: (props) => {
      const data = props.row.original;
      return data.county_code;
    },
  }),

  columnHelper.accessor("congressional_district", {
    id: "congressional_district",
    header: () => "Congressional district",
    cell: (props) => {
      const data = props.row.original;
      return data.congressional_district;
    },
  }),

  columnHelper.accessor("additional_website", {
    id: "additional_website",
    header: () => "Additional website",
    cell: (props) => {
      const data = props.row.original;
      const website = data.additional_website?.indexOf("http") === -1 ? `http://${data.additional_website}` : data.additional_website;
      return (
        website ? (
          <a
            href={website}
            target="_blank"
            rel="noreferrer"
            className="usa-link usa-link--external"
          >
            {website}
          </a>
        ) : ""
      );
    },
  }),

  columnHelper.accessor("cage_code", {
    id: "cage_code",
    header: () => "CAGE code",
    cell: (props) => {
      const data = props.row.original;
      return data.cage_code;
    },
  }),

  columnHelper.accessor("msa", {
    id: "msa",
    header: () => "MSA",
    cell: (props) => {
      const data = props.row.original;
      return data.msa;
    },
  }),

  columnHelper.accessor("year_established", {
    id: "year_established",
    header: () => "Year established",
    cell: (props) => {
      const data = props.row.original;
      return data.year_established;
    },
  }),

  columnHelper.accessor("legal_structure", {
    id: "legal_structure",
    header: () => "Legal structure",
    cell: (props) => {
      const data = props.row.original;
      return getLegalStructureName(data.legal_structure);
    },
  }),

  columnHelper.accessor("current_principals", {
    id: "current_principals",
    header: () => "Current principals",
    cell: (props) => {
      const data = props.row.original;

      if (data?.current_principals) {
        const splitPrincipals = splitByEveryOtherComma(data.current_principals)
        return (
          splitPrincipals.map((principal, i) => {
            return (
              <>
                {principal}
                {i === splitPrincipals.length - 1 ? null : (
                  <>
                    <br />
                    <br />
                  </>
                )}
              </>
            );
          })
        )
      } else return null
    }
  }),

  columnHelper.accessor("meili_self_certifications", {
    id: "meili_self_certifications",
    header: () => "Business type and self-certifications",
    cell: (props) => {
      const data = props.row.original;
      return data?.meili_self_certifications.join(", ");
    }
  }),

  columnHelper.accessor("keywords", {
    id: "keywords",
    header: () => "Keywords",
    cell: (props) => {
      const data = props.row.original;
      return data?.keywords.join(", ");
    },
  }),

  columnHelper.accessor("qas_standards", {
    id: "qas_standards",
    header: () => "QAS standards",
    cell: (props) => {
      const data = props.row.original;
      return data.qas_standards.join(", ");
    },
  }),

  /** 8a Certification Columns */
  columnHelper.accessor("certStatus_8a", {
    id: "certStatus_8a",
    header: () => "8(a) certification status",
    cell: (props) => format.tag(props.row.original.certStatus_8a)
  }),

  columnHelper.accessor("certDateStart_8a", {
    id: "certDateStart_8a",
    header: () => "8(a) entrance date",
    cell: (props) => props.row.original.certDateStart_8a
  }),

  columnHelper.accessor("certDateExit_8a", {
    id: "certDateExit_8a",
    header: () => "8(a) exit date",
    cell: (props) => props.row.original.certDateExit_8a
  }),

  /** 8a JV Certification Columns */
  columnHelper.accessor("certStatus_8aJV", {
    id: "certStatus_8aJV",
    header: () => "8(a) Joint Venture certification status",
    cell: (props) => format.tag(props.row.original.certStatus_8aJV)
  }),

  columnHelper.accessor("certDateStart_8aJV", {
    id: "certDateStart_8aJV",
    header: () => "8(a) Joint Venture entrance date",

    // invalid date for 8a jv
    cell: (props) => props.row.original.certDateStart_8aJV
  }),

  columnHelper.accessor("certDateExit_8aJV", {
    id: "certDateExit_8aJV",
    header: () => "8(a) Joint Venture exit date",

    // invalid date for 8a jv
    cell: (props) => props.row.original.certDateExit_8aJV
  }),

  /** HUBZone Certification Columns */
  columnHelper.accessor("certStatus_HZ", {
    id: "certStatus_HZ",
    header: () => "HUBZone status",
    cell: (props) => format.tag(props.row.original.certStatus_HZ)
  }),

  columnHelper.accessor("certDateStart_HZ", {
    id: "certDateStart_HZ",
    header: () => "HUBZone certification date",
    cell: (props) => props.row.original.certDateStart_HZ
  }),

  /** WOSB Certification Columns */
  columnHelper.accessor("certStatus_WOSB", {
    id: "certStatus_WOSB",
    header: () => "WOSB status",
    cell: (props) => format.tag(props.row.original.certStatus_WOSB)
  }),

  columnHelper.accessor("certDateStart_WOSB", {
    id: "certDateStart_WOSB",
    header: () => "WOSB certification date",
    cell: (props) => props.row.original.certDateStart_WOSB
  }),

  /** EDWOSB Certification Columns */
  columnHelper.accessor("certStatus_EDWOSB", {
    id: "certStatus_EDWOSB",
    header: () => "EDWOSB status",
    cell: (props) => format.tag(props.row.original.certStatus_EDWOSB)
  }),

  columnHelper.accessor("certDateStart_EDWOSB", {
    id: "certDateStart_EDWOSB",
    header: () => "EDWOSB certification date",
    cell: (props) => props.row.original.certDateStart_EDWOSB
  }),

  /** VOSB Certification Columns */
  columnHelper.accessor("certStatus_VOSB", {
    id: "certStatus_VOSB",
    header: () => "VOSB status",
    cell: (props) => format.tag(props.row.original.certStatus_VOSB)
  }),

  columnHelper.accessor("certDateStart_VOSB", {
    id: "certDateStart_VOSB",
    header: () => "VOSB entrance date",
    cell: (props) => props.row.original.certDateStart_VOSB
  }),

  columnHelper.accessor("certDateExit_VOSB", {
    id: "certDateExit_VOSB",
    header: () => "VOSB exit date",
    cell: (props) => props.row.original.certDateExit_VOSB,
  }),

  // columnHelper.accessor("certStatus_VOSBJV", {
  //   id: "certStatus_VOSBJV",
  //   header: () => "VOSB JV status",
  //   cell: (props) => format.tag(props.row.original.certStatus_VOSBJV),
  // }),

  // columnHelper.accessor("certDateStart_VOSBJV", {
  //   id: "certDateStart_VOSBJV",
  //   header: () => "VOSB JV entrance date",
  //   cell: (props) => props.row.original.certDateStart_VOSBJV
  // }),

  /** SDVOSB Certification Columns */
  columnHelper.accessor("certStatus_SDVOSB", {
    id: "certStatus_SDVOSB",
    header: () => "SDVOSB status",
    cell: (props) => format.tag(props.row.original.certStatus_SDVOSB)
  }),

  columnHelper.accessor("certDateStart_SDVOSB", {
    id: "certDateStart_SDVOSB",
    header: () => "SDVOSB entrance date",
    cell: (props) => props.row.original.certDateStart_SDVOSB
  }),

  columnHelper.accessor("certDateExit_SDVOSB", {
    id: "certDateExit_SDVOSB",
    header: () => "SDVOSB exit date",
    cell: (props) => props.row.original.certDateExit_SDVOSB,
  }),

  // columnHelper.accessor("certStatus_SDVOSBJV", {
  //   id: "certStatus_SDVOSBJV",
  //   header: () => "SDVOSB JV status",
  //   cell: (props) => format.tag(props.row.original.certStatus_SDVOSBJV)
  // }),

  // columnHelper.accessor("certDateStart_SDVOSBJV", {
  //   id: "certDateStart_SDVOSBJV",
  //   header: () => "SDVOSB JV entrance date",
  //   cell: (props) => props.row.original.certDateStart_SDVOSBJV
  // }),

  columnHelper.accessor("non_fed_govt_certifications", {
    id: "non_fed_govt_certifications",
    header: () => "Non-Federal government certifications",
    cell: (props) => props.row.original.non_fed_govt_certifications?.split(";")?.join(", ")
  }),


  columnHelper.accessor("naics_primary", {
    id: "naics_primary",
    header: () => "Primary NAICS code",
    cell: (props) => {
      const data = props.row.original;
      return data?.naics_primary;
    },
  }),

  columnHelper.accessor("naics_all_codes", {
    id: "naics_all_codes",
    header: () => "All NAICS codes",
    cell: (props) => {
      const data = props.row.original;
      return data.naics_all_codes?.join(", ");
    },
  }),

  columnHelper.accessor((row) => row.entity_detail_id, {
    id: "naics_small_codes",
    header: () => "“Small” NAICS codes",
    cell: (props) => {
      const data = props.row.original;
      return data.naics_small_codes.join(", ")
    },
  }),

  columnHelper.accessor("business_size", {
    id: "business_size",
    header: () => "Number of employees",
    cell: (props) => {
      const data = props.row.original;
      return data.business_size;
    },
  }),

  columnHelper.accessor("annual_revenue", {
    id: "annual_revenue",
    header: () => "Annual gross revenue",
    cell: (props) => {
      const data = props.row.original;
      return data.annual_revenue ? formatAsCurrency(data.annual_revenue) : null;
    },
  }),

  columnHelper.accessor("special_equip_material", {
    id: "special_equip_material",
    header: () => "Special equpiment/materials",
    cell: (props) => {
      const data = props.row.original;
      return data.special_equip_material;
    },
  }),

  columnHelper.accessor("construction_bonding_per_contract", {
    id: "construction_bonding_per_contract",
    header: () => "Bonding level: Construction, per contract",
    cell: (props) => {
      const data = props.row.original;
      return data.service_bonding_aggregate ? formatAsCurrency(data.construction_bonding_per_contract) : '';
    },
  }),

  columnHelper.accessor("construction_bonding_aggregate", {
    id: "construction_bonding_aggregate",
    header: () => "Bonding level: Construction, aggregate",
    cell: (props) => {
      const data = props.row.original;
      return data.construction_bonding_aggregate ? formatAsCurrency(data.construction_bonding_aggregate) : '';
    },
  }),

  columnHelper.accessor("service_bonding_contract", {
    id: "service_bonding_contract",
    header: () => "Bonding level: Service, per contract",
    cell: (props) => {
      const data = props.row.original;
      return data.service_bonding_contract ? formatAsCurrency(data.service_bonding_contract) : '';
    },
  }),

  columnHelper.accessor("service_bonding_aggregate", {
    id: "service_bonding_aggregate",
    header: () => "Bonding level: Service, aggregate",
    cell: (props) => {
      const data = props.row.original;
      return data.service_bonding_aggregate ? formatAsCurrency(data.service_bonding_aggregate) : '';
    },
  }),

];

export {
  columns,
  optionalColumns
}
